
import { LightningElement, api, track } from 'lwc';

import {getHostOnBoardLabels} from '../../../utils/languageUtil';
import { fetchAuthSession } from 'aws-amplify/auth';
import { get, patch, post } from 'aws-amplify/api';
import { mapTemplateLabels, TEMPLATE_LABELS, getLabelsByLanguage } from '../../../utils/languageUtil';
import { AMENITIES_2 } from '../../../utils/optionsUtil';

// Step 1: Enter Address (with google maps later)  👉  address only shares when reservation is made 

/* Step 2: Select Listing type
        Pool, Sports Court, Yard, Home Space, Something Else 
*/

/* Step 3: Property Info 

        Pool: 
            Step 1:
                Outdoor/Indoor 
                Full-Sized, Framed, Hot Tub, Plunge, Cold Plunge 
                Water System: Chlorine, Salt
                Restroom?: 
                    A. Restroom in main residence
                    B. Guest restroom is in a detached or dedicated house.
                    C. Guest can use portable restroom
                    D. No restroom available

            Step 2: 
                Max Guests (1-200)

            Step 3 Set Price:
                1 - 5 guests $25/hr
                6 - 10 guest: $35
                11 - 15 guests: $45/hr
                16 - 20 guests: $55/hr
                21 - 30 guests: $75/hr
                31 - 40 guests: $100/hr
                41 - 50 guests: $125/hr
                51 - 60 guests: $150/hr
                61 - 70 guests: $175/hr
                71 - 80 guests: $200/hr
                81 - 90 guests: $225/hr
                91 - 100 guests: $250/hr
                101 - 125 guests: $275/hr
                126 - 150 guests: $300/hr
                151 - 175 guests: $325/hr
                176 - 200 guests: $350/hr

            Step 4 (Property Name):
                Enter name for property 

            Step 5 (Add Photos):
                Maximum: 50
                Minimum: 1
                Recommended: 5-10
                                

*/

const PRICE_BUCKET_DISPLAY_VALUE_FORMULA = (val) => `$${val}/hr`;

const normalizeAmenityValues = (amenities) => {
    const values = [];
    const addValue = (value) => {
        const normalizedValue = typeof value === 'string' ? value.trim() : '';
        if (normalizedValue) {
            values.push(normalizedValue);
        }
    };
    const visitAmenity = (amenity) => {
        if (typeof amenity === 'string') {
            addValue(amenity);
            return;
        }

        if (Array.isArray(amenity)) {
            amenity.forEach(visitAmenity);
            return;
        }

        if (!amenity || typeof amenity !== 'object') {
            return;
        }

        if (Array.isArray(amenity.items)) {
            const hasSelectionState = amenity.items.some(item =>
                item && typeof item === 'object' && (
                    'selected' in item ||
                    'checked' in item ||
                    'isSelected' in item
                )
            );
            const selectedItems = hasSelectionState
                ? amenity.items.filter(item => item?.selected || item?.checked || item?.isSelected)
                : amenity.items;

            selectedItems.forEach(visitAmenity);
            return;
        }

        addValue(amenity.value);
    };

    visitAmenity(amenities);
    return [...new Set(values)];
};

const DEFAULT_PRICE_BUCKETS = [
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_1_5', label: `1 - 5 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$25/hr', value: 25.00, dtype: 'currency', minGuests: 1, maxGuests: 5},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_6_10', label: `6 - 10 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$35/hr', value: 35.00, dtype: 'currency', minGuests: 6, maxGuests: 10},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_11_15', label: `11 - 15 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$45/hr', value: 45.00, dtype: 'currency', minGuests: 11, maxGuests: 15},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_16_20', label: `16 - 20 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$55/hr', value: 55.00, dtype: 'currency', minGuests: 16, maxGuests: 20},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_21_30', label: `21 - 30 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$75/hr', value: 75.00, dtype: 'currency', minGuests: 21, maxGuests: 30},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_31_40', label: `31 - 40 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$100/hr', value: 100.00, dtype: 'currency', minGuests: 31, maxGuests: 40},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_41_50', label: `41 - 50 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$125/hr', value: 125.00, dtype: 'currency', minGuests: 41, maxGuests: 50},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_51_60', label: `51 - 60 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$150/hr', value: 150.00, dtype: 'currency', minGuests: 51, maxGuests: 60},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_61_70', label: `61 - 70 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$175/hr', value: 175.00, dtype: 'currency', minGuests: 61, maxGuests: 70},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_71_80', label: `71 - 80 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$200/hr', value: 200.00, dtype: 'currency', minGuests: 71, maxGuests: 80},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_81_90', label: `81 - 90 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$225/hr', value: 225.00, dtype: 'currency', minGuests: 81, maxGuests: 90},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_91_100', label: `91 - 100 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$250/hr', value: 250.00, dtype: 'currency', minGuests: 91, maxGuests: 100},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_101_125', label: `101 - 125 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$275/hr', value: 275.00, dtype: 'currency', minGuests: 101, maxGuests: 125},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_126_150', label: `126 - 150 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$300/hr', value: 300.00, dtype: 'currency', minGuests: 126, maxGuests: 150},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_151_175', label: `151 - 175 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$325/hr', value: 325.00, dtype: 'currency', minGuests: 151, maxGuests: 175},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_176_200', label: `176 - 200 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$350/hr', value: 350.00, dtype: 'currency', minGuests: 176, maxGuests: 200},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_201_225', label: `201 - 225 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$400/hr', value: 400.00, dtype: 'currency', minGuests: 201, maxGuests: 225},
    {displayValueFormula: (val) => `$${val}/hr`, fieldApiName: 'Guest_Price_Bucket_226_250', label: `226 - 250 ${getLabelsByLanguage('guests').toLowerCase()}`, displayValue: '$450/hr', value: 450.00, dtype: 'currency', minGuests: 226, maxGuests: 250},
];
const STEP_KEYS = {
    address: {fields: ['street', 'country', 'state', 'city', 'zip_code', 'latitude', 'longitude'], committed: false},
    basicInfo: {fields: [ 'environment_type', 'pool_type', 'water_system', 'restroom_type'], committed: false}, 
    max_guests: {fields: ['max_guests'], committed: false}, 
    listing_price_bucket: {fields: ['listing_price_bucket'], committed: false}, 
    listing_photo: {fields: ['listing_photo'], committed: false}, 
    property_name: {fields: ['property_name'], committed: false},
    amenities: {fields: ['amenities'], committed: false},
    houseRules: {fields: [
        'infants_allowed',
        'infants_free',
        'alcohol_allowed',
        'smoking_allowed',
        'pets_allowed',
        'parties_allowed',
        'loud_music_allowed',
        'glass_containers_allowed',
        'outside_food_allowed',
        'outside_drinks_allowed',
        'photography_allowed',
        'commercial_filming_allowed'
    ], committed: false}
};

const HOUSE_RULES = [
    {field: 'infants_allowed', label: 'Infants allowed', defaultValue: true},
    {field: 'infants_free', label: 'Infants are free', defaultValue: true},
    {field: 'alcohol_allowed', label: 'Alcohol allowed', defaultValue: false},
    {field: 'smoking_allowed', label: 'Smoking allowed', defaultValue: false},
    {field: 'pets_allowed', label: 'Pets allowed', defaultValue: false},
    {field: 'parties_allowed', label: 'Parties allowed', defaultValue: false},
    {field: 'loud_music_allowed', label: 'Loud music allowed', defaultValue: false},
    {field: 'glass_containers_allowed', label: 'Glass containers allowed', defaultValue: false},
    {field: 'outside_food_allowed', label: 'Outside food allowed', defaultValue: true},
    {field: 'outside_drinks_allowed', label: 'Outside drinks allowed', defaultValue: true},
    {field: 'photography_allowed', label: 'Photography allowed', defaultValue: false},
    {field: 'commercial_filming_allowed', label: 'Commercial filming allowed', defaultValue: false}
];

const EDIT_ONLY_STEPS = {
    amenities: {templateStep: '_isAmenities', label: 'Amenities', headerLabel: 'Step 7: Amenities'},
    houseRules: {templateStep: '_isHouseRules', label: 'House Rules', headerLabel: 'Step 8: House Rules'}
};

const ONBOARD_STEP_KEYS = Object.fromEntries(
    Object.entries(STEP_KEYS).filter(([stepKey]) => !EDIT_ONLY_STEPS[stepKey])
);

const ADDRESS_KEYS = [
    'street',
    'county',
    'city',
    'state',
    'zip_code',
    'country',
    'latitude',
    'longitude',
];
const BASIC_INFO_KEYS = [
    'environment_type',
    'pool_type',
    'water_system',
    'restroom_type',
];

const ALL_KEYS = [
    'street',
    'city',
    'state',
    'zip_code',
    'country',
    'latitude',
    'longitude',
    'environment_type',
    'pool_type',
    'water_system',
    'restroom_type',
    'min_guests',
    'max_guests',
    'listing_price_bucket',
    'property_name',
    'listing_photo',
    'amenities',
    ...HOUSE_RULES.map(rule => rule.field)
];
const STEP_LABELS_MAP = {
    address: 'Address',
    basicInfo: 'Basic Info',
    max_guests: 'Guest Amount',
    listing_price_bucket: 'Pricing',
    listing_photo: 'Upload Photos',
    property_name: 'Property Name',
    amenities: 'Amenities',
    houseRules: 'House Rules',
};


const ONBOARD_SKELETON = {
    address: {
        street: null,
        county: null,
        city: null,
        state: null,
        zip_code: null,
        country: null,
        county: null,
        latitude: null,
        longitude: null,
    },
    basicInfo: {
        environment_type: 'Outdoor',
        pool_type: 'Full Pool',
        water_system: 'Chlorine',
        restroom_type: 'Restroom in main residence',
    },
    min_guests: 1,
    max_guests: 50,    
    listing_price_bucket: DEFAULT_PRICE_BUCKETS.map(bkt => ({
        [bkt.fieldApiName]: bkt.value
    })),
    listing_photo: [],
    property_name: null,
    amenities: [],
    ...Object.fromEntries(HOUSE_RULES.map(rule => [rule.field, rule.defaultValue]))
};


const PROGRESS_STEPS = [
    {stepKey: 'address', label: 'Address', value: 'Address', templateStep: '_isAddress', headerLabel: 'Step 1: Address',},
    {stepKey: 'basicInfo', label: 'Basic Info', value: 'Basic Info', templateStep: '_isBasicInfo', headerLabel: 'Step 2: Basic info',},
    {stepKey: 'max_guests', label: 'Guest Amount', value: 'Guest Amount', templateStep: '_isGuestAmount', headerLabel: 'Step 3: Guests',}, 
    {stepKey: 'listing_price_bucket', label: 'Pricing', value: 'Pricing', templateStep: '_isPricing', headerLabel: 'Step 4: Pricing',},
    {stepKey: 'listing_photo', label: 'Upload Photos', value: 'Upload Photos', templateStep: '_isUploadPhotos', headerLabel: 'Step 5: Upload photos'},
    {stepKey: 'property_name', label: 'Property Name', value: 'Property Name', templateStep: '_isPropertyName', headerLabel: 'Step 6: Listing name',},
];

const NOT_REQUIRED_FIELDS = [
    'latitude',
    'longitude',
    'county'
]

// If just updating one section, send baseFields as --> {stepKey: <name>, fields: {<fieldApiName>: <value>, ...}}
function updateStepTracker(stepTracker, baseFields, isSection=false) {
    if (!baseFields) {
        return stepTracker;
    }
    if (isSection) {
        const stepKey = baseFields?.stepKey;
        const stepTrackerFields = stepTracker?.[stepKey]?.fields;
        const fieldValsMap = baseFields.fields;
        const committed = stepTrackerFields.every(f => 
            f === 'listing_photo' || f === 'listing_price_bucket' || f === 'amenities'
                ? fieldValsMap?.[f]?.length > 0 
                : HOUSE_RULES.some(rule => rule.field === f)
                    ? typeof fieldValsMap?.[f] === 'boolean'
                : NOT_REQUIRED_FIELDS.includes(f) || !!fieldValsMap?.[f]
        );

        return {
            ...stepTracker,
            [stepKey]: {fields: stepTrackerFields, committed}
        };
    }

    return Object.fromEntries(
        Object.entries(stepTracker).map(([section, details]) => {
            const fields = details.fields;
            const newDetails = {
                fields, 
                committed: fields.every(f => 
                    f === 'listing_photo' || f === 'listing_price_bucket' || f === 'amenities'
                        ? baseFields?.[f]?.length > 0 
                        : HOUSE_RULES.some(rule => rule.field === f)
                            ? typeof baseFields?.[f] === 'boolean'
                        : NOT_REQUIRED_FIELDS.includes(f) || !!baseFields?.[f]
                    )
            }
            return [section, newDetails]
        })
    );
}

function normalizeComparableValue(value) {
    if (Array.isArray(value)) {
        return value.map((item, index) => {
            if (item?.s3_key || item?.file) {
                return {
                    s3_key: item.s3_key || item.file?.name || item.name || null,
                    sort_order: index,
                    is_primary: index === 0
                };
            }
            return Object.fromEntries(
                Object.entries(item || {}).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
            );
        });
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        );
    }

    return value ?? null;
}

function areComparableValuesEqual(valueA, valueB) {
    return JSON.stringify(normalizeComparableValue(valueA)) === JSON.stringify(normalizeComparableValue(valueB));
}

// ⚠️ Watch this --> set to false when have internet ⚠️
import { IS_TESTING_NO_INTERNET } from '../../../utils/basicUtils';



export default class getWetHostOnBoard extends LightningElement {
    isTest = IS_TESTING_NO_INTERNET;
    _isLoading = false;

    // 📝 Main From
    onboardForm = Object.fromEntries(
        Object.entries(ONBOARD_SKELETON).map(([fieldApiName, value]) =>  
            [fieldApiName, value]
        )
    );

    /*fileTitlesFn = (_, index) => {
        return index === 0
            ? {label: `Primary Photo (${index+1})`}
            : {label: `${index+1}`};
    }*/
    fileTitlesFn = (_, index) => {
        return index === 0
            ? {badgeCl: 'gw-theme_brand', label: `Primary`}
            : {badgeCl: 'slds-badge_inverse', label: `${index+1}`};
    }
    
    userOptions = [
        {label: 'Account Details', value: 'accountDetails'},
        {label: 'Activity', value: 'activity'},
        {label: 'Messages', value: 'messages'},
        {label: 'Log out', value: 'logOut'},
    ];

    templateLabels = mapTemplateLabels(TEMPLATE_LABELS);
    labels = getHostOnBoardLabels();


    
    _stepTracker = ONBOARD_STEP_KEYS;

    _cardsWrapperCl = 'slds-col slds-p-horizontal_medium slds-small-size_12-of-12 slds-medium-size_7-of-12';
    @api 
    set isModal(val) {
        this._isModal = val;
        if (val) {
            this._cardsWrapperCl = 'slds-col slds-p-horizontal_medium slds-small-size_12-of-12 slds-medium-size_12-of-12';
        }
    }
    get isModal() {
        return this._isModal;
    }

    // Gets the user's draft listings
    _sessionUser;
    @api 
    set sessionUser(val) {
        this._sessionUser = val;
        if (val && !this.isTest && !this._listingId) {
            Promise.resolve().then(async () => {
                await this.loadSessionUserOnSetter();
            });
        }
    }
    get sessionUser() {
        return this._sessionUser;
    }

    async loadSessionUserOnSetter(listingId=null) {
        await this.loadMe(listingId);
    }

    // Initiatilize "Edit Listing" if parent sends id of existing listing instead of user
    _listingId;
    // Flagged true when the listing id is provided rather than retrieved
    _isExternalListingId = false; 
    @api 
    set listingId(val) {
        this._listingId = val;
        this._isExternalListingId = !!val;
        if (!val || this.isTest) return;
        Promise.resolve().then(async () => {
            await this.loadSessionUserOnSetter(val);
        });
    }
    get listingId() {
        return this._listingId;
    }

    _initialStepKey;
    @api
    set initialStepKey(val) {
        this._initialStepKey = val;
        if (val) {
            this.navToStepByKey(val);
        }
    }
    get initialStepKey() {
        return this._initialStepKey;
    }

    _originalListing;
    // Load user info 
    async loadMe(listingId=null) {
        this._isLoading = true;
        console.log('has listing id in loadMe() of getWetHostOnBoard.js? ', listingId);
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }   
            const op = get({
                apiName: 'api',
                path: listingId 
                    ? `/cognito/listing-by-id?listingId=${listingId}` 
                    : '/cognito/host-onboard',
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                },
            });

            const { body } = await op.response;
            const payload = await body.json();
            const listing = payload?.listing || payload;
            console.log('here is listing in getWetHostOnBoard.js: ', listing);
            // Flag steps as completed
            this._stepTracker = updateStepTracker(this._stepTracker, listing);

            this._listingId = listing?.id ?? null;
            this._isNew = !this._listingId;
            this.mapObjToForm(listing);
            this._originalListing = this.getComparablePayload(listing);
            if (this._initialStepKey) {
                this.navToStepByKey(this._initialStepKey);
            }
            
        } catch (error) {
            console.error('Error loading user profile', error);
        } finally {
            this._isLoading = false;
        }
    }   
    // Handle saved values
    _firstDone=false;
    addressVal;
    mapObjToForm(form) {
        try {
            if (form) {
                Object.entries(form)
                    .forEach(([key, value]) => {
                        if (ALL_KEYS.includes(key)) {
                            if (ADDRESS_KEYS.includes(key)) {
                                this.onboardForm = {
                                    ...this.onboardForm,
                                    address: {...this.onboardForm.address, [key]: value}
                                };
                            } else if (BASIC_INFO_KEYS.includes(key)) {
                                this.onboardForm = {
                                    ...this.onboardForm,
                                    basicInfo: {...this.onboardForm.basicInfo, [key]: value}
                                };

                                const isValid = key === 'pool_type' && value && 
                                    this.poolTypeButtonStates.some(o => o.value === value);

                                if (isValid) {
                                    this.poolTypeButtonStates = this.poolTypeButtonStates.map(obj => {
                                        return {...obj, selected: obj.value === value};
                                    });
                                }
                            } else if (key === 'listing_photo') {
                                this.onboardForm = {
                                    ...this.onboardForm,
                                    listing_photo: Array.isArray(value)
                                        ? value.map((photo) => ({
                                            keyId: photo.id,
                                            url: photo.view_url,
                                            name: photo.s3_key?.split('/').pop(),
                                            s3_key: photo.s3_key,
                                            sort_order: photo.sort_order,
                                            is_primary: photo.is_primary
                                        }))
                                        : []
                                };

                            } else {
                                this.onboardForm = {
                                    ...this.onboardForm,
                                    [key]: value
                                }
                            }
                        }
                    });
                    
                if (!this._isNew && this.onboardForm.address) {
                    this.addressVal = this.onboardForm?.address ?? null;
                }
            }
            
        } catch(error) {
            console.error('Error in mapObjToForm() of getWetHostOnBoard.js', JSON.stringify(error));
        }
    }

    getComparablePayload(source = null) {
        const base = source || {
            street: this.onboardForm.address.street,
            county: this.onboardForm.address.county,
            city: this.onboardForm.address.city,
            state: this.onboardForm.address.state,
            zip_code: this.onboardForm.address.zip_code,
            country: this.onboardForm.address.country,
            latitude: this.onboardForm.address.latitude,
            longitude: this.onboardForm.address.longitude,
            environment_type: this.onboardForm.basicInfo.environment_type,
            pool_type: this.onboardForm.basicInfo.pool_type,
            water_system: this.onboardForm.basicInfo.water_system,
            restroom_type: this.onboardForm.basicInfo.restroom_type,
            min_guests: this.onboardForm.min_guests,
            max_guests: this.onboardForm.max_guests,
            property_name: this.onboardForm.property_name,
            listing_price_bucket: this.onboardForm.listing_price_bucket,
            listing_photo: this.onboardForm.listing_photo,
            amenities: normalizeAmenityValues(this.onboardForm.amenities),
            ...Object.fromEntries(HOUSE_RULES.map(rule => [rule.field, this.onboardForm[rule.field]]))
        };

        return Object.fromEntries(
            ALL_KEYS.map(field => [field, normalizeComparableValue(base?.[field])])
        );
    }

    @api getEditedFieldsForStep(stepKey) {
        this.handleUpdates(this.progressSteps.findIndex(step => step.stepKey === stepKey));

        const original = this._originalListing || {};
        const current = this.getComparablePayload();
        const fields = STEP_KEYS?.[stepKey]?.fields || [];

        return fields.filter(field => !areComparableValuesEqual(original[field], current[field]));
    }

    @api hasStepChanges(stepKey) {
        return this.getEditedFieldsForStep(stepKey).length > 0;
    }

    async uploadFileToS3(file) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
            throw new Error('No idToken available');
        }

        const op = post({
            apiName: 'api',
            path: '/cognito/uploads/presign',
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: {
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream'
                }
            }
        });

        const { body } = await op.response;
        const payload = await body.json();

        const uploadRes = await fetch(payload.uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type || 'application/octet-stream'
            },
            body: file
        });

        if (!uploadRes.ok) {
            throw new Error(`S3 upload failed: ${uploadRes.status}`);
        }

        return payload.key;
    }
    async saveListingPhoto(listingId, s3Key, isPrimary = false, sortOrder = 0) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        const op = post({
            apiName: 'api',
            path: '/cognito/listing-photos',
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: {
                    listingId,
                    s3Key,
                    isPrimary,
                    sortOrder
                }
            }
        });

        const { body } = await op.response;
        return await body.json();
    }


    _isLoading = false;
    _listingPhotoFiles = [];

    isListingReadyForReview() {
        const address = this.onboardForm.address || {};
        const basicInfo = this.onboardForm.basicInfo || {};

        const hasAddress = STEP_KEYS.address.fields.every(field => 
            !!address[field]
        );

        const hasBasicInfo = STEP_KEYS.basicInfo.fields.every(field => !!basicInfo[field]);

        const hasGuests = !!this.onboardForm.max_guests;

        const hasPricing = Array.isArray(this.onboardForm.listing_price_bucket)
            && this.onboardForm.listing_price_bucket.length > 0;

        const hasPhotos = Array.isArray(this.onboardForm.listing_photo)
            && this.onboardForm.listing_photo.length > 0;

        const hasPropertyName = !!this.onboardForm.property_name;

        return hasAddress
            && hasBasicInfo
            && hasGuests
            && hasPricing
            && hasPhotos
            && hasPropertyName;
    }

    // Save user listing 
    @api async saveListing(options = {}) {
        this._isLoading = true;
        let hadError = false;
        let submittedForReview = false;
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }

            const rawPhotos = Array.isArray(this.onboardForm.listing_photo)
                ? this.onboardForm.listing_photo
                : [];

            const uploadedPhotos = [];

            for (const [index, photo] of rawPhotos.entries()) {
                if (photo?.s3_key) {
                    uploadedPhotos.push({
                        s3_key: photo.s3_key,
                        sort_order: index,
                        is_primary: index === 0
                    });
                    continue;
                }

                const file = photo?.file || photo;
                if (!(file instanceof File)) {
                    continue;
                }

                const s3Key = await this.uploadFileToS3(file);

                uploadedPhotos.push({
                    s3_key: s3Key,
                    sort_order: index,
                    is_primary: index === 0
                });
            }

            const payload = {
                street: this.onboardForm.address.street,
                county: this.onboardForm.address.county,
                city: this.onboardForm.address.city,
                state: this.onboardForm.address.state,
                zip_code: this.onboardForm.address.zip_code,
                country: this.onboardForm.address.country,
                latitude: this.onboardForm.address.latitude,
                longitude: this.onboardForm.address.longitude,
                environment_type: this.onboardForm.basicInfo.environment_type,
                pool_type: this.onboardForm.basicInfo.pool_type,
                water_system: this.onboardForm.basicInfo.water_system,
                restroom_type: this.onboardForm.basicInfo.restroom_type,
                min_guests: this.onboardForm.min_guests,
                max_guests: this.onboardForm.max_guests,
                property_name: this.onboardForm.property_name,
                listing_price_bucket: this.onboardForm.listing_price_bucket,
                listing_photo: uploadedPhotos,
                amenities: normalizeAmenityValues(this.onboardForm.amenities),
                ...Object.fromEntries(HOUSE_RULES.map(rule => [rule.field, this.onboardForm[rule.field]])),
                listingId: this._listingId,
            };

            submittedForReview = options.submitForReview === true && this.isListingReadyForReview();
            if (options.status) {
                payload.status = options.status;
            } else if (submittedForReview || !this._isExternalListingId) {
                payload.status = submittedForReview ? 'pending approval' : 'draft';
            }

            const editedFields = Array.isArray(options.editedFields)
                ? options.editedFields
                : [];
            const editedSections = Array.isArray(options.editedSections)
                ? options.editedSections
                : [];

            if (editedFields.length) {
                payload.edited_fields = editedFields;
            }
            if (editedSections.length) {
                payload.edited_sections = editedSections;
            }
            
            const serverPath = this._isExternalListingId 
                ? `/cognito/listing-by-id?listingId=${this._listingId}`
                : '/cognito/host-onboard';

            const op = patch({
                apiName: 'api',
                path: serverPath,
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: payload
                }
            });

            const { body } = await op.response;
            const result = await body.json();
            //console.log('here is result in saveListing() of getWetHostOnBoard.js: ', JSON.stringify(result));
            const listingId = result?.listing?.id || result.listingId || result?.id || this._listingId;

            if (!listingId) {
                this.showToast(
                    'Failed to save listing', 
                    'Something went wrong and we were unable to save your listing, contact us at getwetinc777@gmail.com',
                    'error'
                );
                hadError = true;
                return;
            }

            this._listingId = listingId;
            this.onboardForm.listing_photo = uploadedPhotos;
            this._originalListing = this.getComparablePayload(payload);

            if (this._isExternalListingId) {
                return payload;
            }

        } catch (error) {
            hadError = true;
            console.error('Error in saveListing() of getWetHostOnBoard.js', error);
            this.showToast(
                'Failed to save listing', 
                'Something went wrong and we were unable to save your listing, contact us at getwetinc777@gmail.com',
                'error'
            );
            try {
                const serverBody = await error?.response?.body?.json?.();
                console.error('SERVER ERROR BODY:', serverBody);
            } catch (e) {
                hadError = true;
                console.error('Could not parse server error body', e);
            }
        } finally {
            this._isLoading = false;
            if (!hadError && options.suppressSuccessToast !== true) {
                this.showToast(
                    submittedForReview ? 'Listing submitted for review!' : 'Listing successfully saved!', 
                    submittedForReview
                        ? 'We sent you an email confirming that your listing is under review. Please allow 1-3 days for us to review it.'
                        : 'Your listing draft was saved.',
                    'success'
                );
                if (options.suppressExit !== true) {
                    setTimeout(() => {
                        this.exitHostOnboardPage();
                    }, 3000);
                }
            }
        }
    }   

    
    // Track whether user made it to the end of the form to track progress
    _isNew = true; 
    // If the details object has preFilled: true --> the step as complete if the page was visited 
    // If not preFilled --> check for null values
    get progressRing() {
        const keysLength = Object.keys(this._stepTracker).length;

        const finished = Object.entries(this._stepTracker)
            .filter(([_, details]) => details.committed);

        let i = 0;
        const checksArr = Object.entries(this._stepTracker)
            .map(([section, details]) => ({
                label: STEP_LABELS_MAP[section],
                completed: details.committed, 
                keyId: `progress-ring-${i++}`,
                step: String(i),
                value: section
            }));

        return !finished?.length 
            ? {
                label: `0/${keysLength}`, 
                value: null, 
                checksArr,
            }
            : {
                label: `${finished.length}/${keysLength}`, 
                value: (finished.length/keysLength) * 100,
                checksArr
            }
    }


    _isAddress = true;
    _isBasicInfo = false;
    _isGuestAmount = false;
    _isPricing = false;
    _isUploadPhotos = false;
    _isPropertyName = false;
    _isAmenities = false;
    _isHouseRules = false;

    get nextStepButtonLabel() {
        const stepIdx = this.progressSteps.findIndex(step => this.currentStep === step.value);
        return stepIdx === this.progressSteps.length - 1
            ? 'Submit for Review'
            : this.templateLabels.saveAndContinue;
    }
    
    
    addressRequirements = {
        street: (val) => val.length >= 2,
        county: (val) => val.length >= 2,
        city: (val) => val.length >= 2,
        state: (val) => val.length >= 2,
        zip_code: (val) => val.length >= 2,
        country: (val) => val.length >= 2,
    };
    // Icon button state for 
    poolTypeButtonStates = [
        {selected: true, value: 'Full Pool', variant: 'full-pool', label: this.labels['Full Pool']},
        {selected: false, value: 'Framed Pool', variant: 'framed-pool', label: this.labels['Framed Pool']},
        {selected: false, value: 'Hot Tub', variant: 'hot-tub', label: this.labels['Hot Tub']},
        {selected: false, value: 'Cold Plunge', variant: 'cold-plunge', label: this.labels['Cold Plunge']},
    ];  

    _largeSvgW = '30rem';
    _largeSvgH = '20rem';
    _svgHW = '34';

    _mobileFalse = true;
    @api 
    set mobile(value) {
        this._mobile = value;
        if (value) {
            this._largeSvgW = '20rem';
            this._largeSvgH = '18rem';
            this._svgHW = '24';
            this._mobileFalse = false;
        }
    } 
    get mobile() {
        return this._mobile;
    }

    _isModalOpen = false;
    renderedCallback() {
        //if (!this._sessionUser && !this._isModalOpen) {
        //    const loginSignUpModal = this.template.querySelector('x-get-wet-sign-up-modal');
        //    if (loginSignUpModal) {
        //        loginSignUpModal.open();
        //    }
        //}
    }


    // Track Step number label 
    _stepHeaderLabel = 'Step 1: Address' ;
    // Track the page according to the currentSteps 
    _templateStep = '_isAddress';
    // Current step for progress indicator 
    currentStep = 'Address';
    // Progress for listing
    progressSteps = PROGRESS_STEPS.map(step => ({
        ...step, 
        label: getLabelsByLanguage(step.label) ?? step.label,
        headerLabel: getLabelsByLanguage(step.headerLabel) ?? step.headerLabel
    }));
    pickers = [
        {label: 'Cold Plunge', value: 'coldPlunge', icon: 'utility:frozen'}
    ];
    waterSystems = [
        {label: 'Chlorine', value: 'Chlorine'},
        {label: 'Saltwater', value: 'Saltwater'}
    ];
    restroomOptions = [
        {label: 'Restroom in main residence', value: 'Restroom in main residence'},
        {label: 'Detached restroom', value: 'Detached restroom'},
        {label: 'Portable restroom', value: 'Portable restroom'},
        {label: 'No restroom', value: 'No restroom'}
    ];
    hostPoolPricing = DEFAULT_PRICE_BUCKETS.map(bucket => {
        const labelRaw = bucket.label; 
        const label = labelRaw.replace('guests', getLabelsByLanguage('guests').toLowerCase());
        return {...bucket, label};
    });

    get stepSubtitle() {
        if (this._isBasicInfo) return this.labels.environment_type__label;
        if (this._isAmenities) return 'Select all the amenities guests can use at your space.';
        if (this._isHouseRules) return 'Choose what guests may bring or do at your space.';
        if (this._isGuestAmount) return this.labels.max_guests__subtitle;
        if (this._isPricing) return this.labels.hostPoolPrice__subtitle;
        if (this._isUploadPhotos) return this.labels.listing_photo__subtitle;
        if (this._isPropertyName) return this.labels.property_name__subtitle;
        return null;
    }

    get amenityOptionGroups() {
        const selectedAmenities = new Set(normalizeAmenityValues(this.onboardForm.amenities));

        return AMENITIES_2.map((group, groupIndex) => ({
            ...group,
            groupId: `amenity-category-${groupIndex}`,
            items: (Array.isArray(group.items) ? group.items : []).map((amenity, amenityIndex) => ({
                ...amenity,
                checkboxId: `amenity-checkbox-${groupIndex}-${amenityIndex}`,
                selected: selectedAmenities.has(amenity.value)
            }))
        })).filter(group => group.items.length);
    }

    get houseRuleOptions() {
        return HOUSE_RULES.map(rule => ({
            ...rule,
            checkboxId: `house-rule-checkbox-${rule.field}`,
            selected: this.onboardForm[rule.field] === true
        }));
    }

    
    _hostPriceBuckets = [];
    propertyName;

    exitHostOnboardPage() {
        try {
            this.dispatchEvent(new CustomEvent('onboardclose', { detail: 'onboardclose' } ));
        } catch(error) {
            console.error('Error in saveState() of getWetHostOnBoard.js', error);
        }
    }

    navToStep(event) {
        const activeStep = event.target?.dataset?.value 
            ?? event.target?.currentTarget?.value 
        

        console.log('here is active step in navToStep() of getWetHostOnBoard.js: ', activeStep);
        const progStepVals = this.progressSteps.map(p => p.value);
        console.log('here is all progress step values: ', JSON.stringify(progStepVals));
        this.navToStepByKey(activeStep);
    }
    @api navToStepByKey(activeStep) {
        if (!activeStep) return;

        const editOnlyStep = EDIT_ONLY_STEPS[activeStep];
        if (editOnlyStep) {
            this._templateStep = editOnlyStep.templateStep;
            this.currentStep = editOnlyStep.label;
            this._stepHeaderLabel = editOnlyStep.headerLabel;
            this.updateTemplateBools();
            return;
        }

        const stepInfo = this.progressSteps.find(step => 
            activeStep === step.stepKey ||
            activeStep === step.value ||
            activeStep === step.label
        );

        if (!stepInfo) return;

        this._templateStep = stepInfo.templateStep;
        this.currentStep = stepInfo.value;
        this._stepHeaderLabel = stepInfo.headerLabel;
        this.updateTemplateBools();
    }
    @api toPreviousStep() {
        const stepIdx = this.progressSteps.findIndex(step => this.currentStep === step.value);

        this.handleUpdates();

        if (stepIdx === 0) {
            const confirmed = window.confirm('Leave page? All changes will be lost.');
            if (confirmed) {
                this.exitHostOnboardPage();
            }
            return;
        }
        const prevStepInfo = this.progressSteps[stepIdx -1];
        this._templateStep = prevStepInfo?.templateStep;
        this.currentStep = prevStepInfo?.value;
        this._stepHeaderLabel = prevStepInfo?.headerLabel;
        this.updateTemplateBools();
    }
    @api async toNextStep() {
        try {
            const stepIdx = this.progressSteps.findIndex(step => this.currentStep === step.value);

            this.handleUpdates(stepIdx);

            if (stepIdx === this.progressSteps.length - 1) {
                await this.saveListing({submitForReview: true});
                return;
            }

            const nextStepInfo = this.progressSteps[stepIdx + 1];
            this._templateStep = nextStepInfo?.templateStep;
            this.currentStep = nextStepInfo?.value;
            this._stepHeaderLabel = nextStepInfo?.headerLabel;
            this.updateTemplateBools();

        } catch(error) {
            console.error('Error in toNextStep() of getWetHostOnBoard.js: ', error, 'error');
        }
    }

    // Property Address - street, city, state, zip_code, country
    handleAddressChange(message) {
        const {fieldApiName, value} = message.detail;
        this.onboardForm = {
            ...this.onboardForm,
            address: {...this.onboardForm.address, [fieldApiName]: value}
        };
    }
    handlePlaceChange(message) {
        const place = message.detail;
        const address = {
            ...this.onboardForm.address,
            street: place?.street || null,
            country: place?.country || null,
            state: place?.state || null,
            city: place?.city || null,
            zip_code: place?.zip_code || null,
            latitude: place?.latitude ?? null,
            longitude: place?.longitude ?? null
        };

        this.onboardForm = {...this.onboardForm, address};

        this.addressVal = address;
    }

    // Basic Info - Outdoor/Indoor
    handlePoolEnvironment(message) {
        const newVal = message?.detail;
        console.log('here is new val in handlePoolEnvironment() of getWetHostOnBoard.js: ', newVal);
        this.onboardForm = {
            ...this.onboardForm,
            basicInfo: {...this.onboardForm.basicInfo, environment_type: newVal}
        };
    }
    // Basic Info - Full Pool, Framed Pool, Hot Tub, Cold Plunge
    handlePoolType(event) {
        const poolType = event.currentTarget.dataset.value;
        if (poolType) {
           this.poolTypeButtonStates = this.poolTypeButtonStates.map(pt => {
                if (pt.value === poolType) {
                    return  {...pt, selected: true}
                }
                return pt.selected ? {...pt, selected: false} : pt;
           });
        }
        this.onboardForm = {
            ...this.onboardForm, basicInfo: {
                ...this.onboardForm.basicInfo, pool_type: poolType
            }
        }
    }
    // Guest Amount - Max Guests
    handleGuestAmount(message) {
        if (message?.detail) {
            this.onboardForm = {
                ...this.onboardForm,
                max_guests: message.detail
            }
            this.updateHostPoolPricing();
        }
    }
    handleWaterSystem(message) {
        const val = message.detail;
        if (val) {
            this.onboardForm = {
                ...this.onboardForm,
                basicInfo: {...this.onboardForm.basicInfo, water_system: val}
            }
        }
    }
    handleRestroom(message) {
        const val = message?.detail;
        if (val) {
            this.onboardForm = {
                ...this.onboardForm,
                basicInfo: {...this.onboardForm.basicInfo, restroom_type: val}
            }
        }
    }
    handleAmenityChange(event) {
        event.currentTarget.focus();

        const amenity = event.currentTarget.dataset.value;
        if (!amenity) return;

        const selectedAmenities = new Set(normalizeAmenityValues(this.onboardForm.amenities));
        if (event.target.checked) {
            selectedAmenities.add(amenity);
        } else {
            selectedAmenities.delete(amenity);
        }
        this.onboardForm = {
            ...this.onboardForm,
            amenities: [...selectedAmenities]
        };
    }
    handleHouseRuleChange(event) {
        event.currentTarget.focus();
        const field = event.currentTarget.dataset.field;
        if (!HOUSE_RULES.some(rule => rule.field === field)) return;

        this.onboardForm = {
            ...this.onboardForm,
            [field]: event.target.checked
        };
    }
    // Pricing - Update price buckets when moving between steps
    handleUpdates(stepIdx) {
        if (this._isGuestAmount) {
            this.updateHostPoolPricing();
        }
        if (this._isPricing) {
            const form = this.template.querySelector('x-gw-form-element-inline-edit');
            if (form) {
                console.log('got form in handleUpdates() of getWetHostOnBoard.js');
                const fields = form.getValues();
                if (fields) {
                    console.log('here is _hostPriceBuckets in handleUpdates() of getWetHostOnBoard.js: ', JSON.stringify(this._hostPriceBuckets));
                    const arr = this._hostPriceBuckets?.length 
                        ? this._hostPriceBuckets 
                        : DEFAULT_PRICE_BUCKETS;

                    this._hostPriceBuckets = arr.map(bucket => {
                        const value = fields[bucket.fieldApiName];
                        return !value 
                            ? bucket
                            : {
                                ...bucket, 
                                value, 
                                displayValue: PRICE_BUCKET_DISPLAY_VALUE_FORMULA(value)
                            };
                    });
                }
                this.updateHostPoolPricing();
            }
        }
        if ( (stepIdx && stepIdx !== -1) || stepIdx === 0 ) {
            const stepKey = this.progressSteps[stepIdx]?.stepKey;
            if (stepKey) {
                const fieldsMap = this.onboardForm?.[stepKey];
                if (!Array.isArray(fieldsMap) && typeof fieldsMap === 'object') {
                    this._stepTracker = updateStepTracker(
                        this._stepTracker, 
                        {stepKey, fields: fieldsMap}, 
                        true
                    );
                } else {
                    this._stepTracker = updateStepTracker(
                        this._stepTracker, 
                        {stepKey, fields: {[stepKey]: fieldsMap}},
                        true
                    );
                }
            }
        }
    }
    
    // Pricing - Price per hour & number of guests
    updateHostPoolPricing() {
        const maxGuests = this.onboardForm.max_guests ?? 50;
        // Use price buckets from db if avaialable -->   else use default price buckets
        const priceBuckets = this._hostPriceBuckets?.length 
            ? this._hostPriceBuckets 
            : DEFAULT_PRICE_BUCKETS;
        
        const newBuckets = priceBuckets
            .filter(bucket => bucket.minGuests <= maxGuests)
            .map(bucket  => {
                if (bucket.maxGuests > maxGuests) {
                    return {
                        ...bucket, 
                        label: `${bucket.minGuests} - ${maxGuests} ${getLabelsByLanguage('guests').toLowerCase()}`,
                        maxGuests: maxGuests
                    };
                }
                return bucket;
            });

        
        const valBuckets = newBuckets.map(bkt => ({[bkt.fieldApiName]: bkt.value}));
        this.hostPoolPricing = newBuckets;
        this.onboardForm = {
            ...this.onboardForm,
            listing_price_bucket: valBuckets
        }
    }
    
    // Listing Photos - add image to array
    handleListingPhotos(message) {
        try {
            const files = message.detail;
            
            const filesClnd = Array.isArray(files) 
                ? (files ?? []).map(f => ({
                    s3_key: f?.s3_key ?? null,
                    name: f.name,
                    url: f.url, 
                    keyId: f.keyId,
                    file: f.file
                })).filter(Boolean)
                : [];

            this.onboardForm = {
                ...this.onboardForm,
                listing_photo: filesClnd
            }
        } catch(error) {
            console.error('Error in handleListingPhotos() of getWetHostOnBoard.js', error);
        }
    }

    // Property Name 
    handlePropertyName(message) {
        const name = message.detail?.value;
        if (name) {
            this.onboardForm = {
                ...this.onboardForm,
                property_name: name
            };
        }
    }
    
    updateTemplateBools() {
        try {
            this._isAddress = this._templateStep === '_isAddress';
            this._isListingType = this._templateStep === '_isListingType';
            this._isBasicInfo = this._templateStep === '_isBasicInfo';
            this._isGuestAmount = this._templateStep === '_isGuestAmount';
            this._isPricing = this._templateStep === '_isPricing';
            this._isUploadPhotos = this._templateStep === '_isUploadPhotos';
            this._isPropertyName = this._templateStep === '_isPropertyName';
            this._isAmenities = this._templateStep === '_isAmenities';
            this._isHouseRules = this._templateStep === '_isHouseRules';
        } catch(error) {
            console.error('Error in updateTemplateBools() of getWetHostOnBoard.js: ', error, 'error');
        }
    }


    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode 
            ? mode 
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';
        
        this.toastObj = { 
            variant: variant, 
            mode: modeP, 
            message: message,
            header: { 
                text: title
            }        
        };
    }
}
